/** Nivel de una cadena, reducido a lo que importa para validarla. */
export interface NivelCadena {
  clienteId: number | null;
  nivel: number;
  activo: boolean;
  destinatarios: number;
}

/** Problema detectado en una cadena de avisos. */
export interface FalloCadena {
  clienteId: number;
  tipo: 'hueco' | 'sin_destinatarios';
  nivel: number;
  /** Explicación lista para mostrar, sin el nombre del cliente. */
  descripcion: string;
}

/**
 * Reglas de una cadena de escalamiento.
 *
 * Los niveles se recorren en orden, así que la cadena sólo funciona si es
 * continua: si existe el nivel 3 pero falta el 2, el aviso se detiene ahí sin
 * que nadie lo note. Y un nivel sin destinatarios es un eslabón que no avisa a
 * nadie aunque exista.
 *
 * Vive en el dominio y no en la pantalla porque es una regla del negocio: si
 * mañana hiciera falta comprobarla en un reporte o antes de guardar, el sitio
 * es este.
 */
export class CadenaEscalamiento {
  constructor(private readonly niveles: readonly NivelCadena[]) {}

  /** Todos los fallos encontrados, agrupados por cliente. */
  detectarFallos(): FalloCadena[] {
    const fallos: FalloCadena[] = [];

    for (const [clienteId, niveles] of this.agruparPorCliente()) {
      const numeros = niveles.map((n) => n.nivel);
      const maximo = Math.max(...numeros);

      // Sólo son huecos los que quedan por debajo del nivel más alto
      // definido: no tener un nivel 4 no es un fallo, es una cadena corta.
      for (let n = 1; n < maximo; n += 1) {
        if (!numeros.includes(n)) {
          fallos.push({
            clienteId,
            tipo: 'hueco',
            nivel: n,
            descripcion: `falta el nivel ${n}, la cadena se corta ahí`,
          });
        }
      }

      for (const nivel of niveles) {
        if (nivel.destinatarios === 0) {
          fallos.push({
            clienteId,
            tipo: 'sin_destinatarios',
            nivel: nivel.nivel,
            descripcion: `el nivel ${nivel.nivel} no avisa a nadie`,
          });
        }
      }
    }

    return fallos.sort((a, b) => a.clienteId - b.clienteId || a.nivel - b.nivel);
  }

  get estaCompleta(): boolean {
    return this.detectarFallos().length === 0;
  }

  /**
   * Sólo se agrupan los niveles activos: uno desactivado se salta a propósito
   * y no debe contar como hueco.
   */
  private agruparPorCliente(): Map<number, NivelCadena[]> {
    const mapa = new Map<number, NivelCadena[]>();

    for (const nivel of this.niveles) {
      if (nivel.clienteId === null || !nivel.activo) continue;
      mapa.set(nivel.clienteId, [...(mapa.get(nivel.clienteId) ?? []), nivel]);
    }

    return mapa;
  }
}
